import classNames from 'classnames';
import { type FC, useContext, useEffect } from 'react';

import CustomSelect from '@/components/FormFields/CustomSelect';
import Confido from '@/components/Icons/Confido';
import useNoodleApi from '@/hooks/useNoodleApi';
import teamsContext from '@/providers/Teams/TeamsContext';
import { getConfidoBankAccounts } from '@/tsClient/creators';

import s from './ConfidoBankAccountSelector.module.scss';

type Props = {
  value?: string;
  onChange: (newValue: string | null) => void | Promise<void>;
  className?: string;
};

const ConfidoBankAccountSelector: FC<Props> = ({ value, onChange, className }) => {
  const { creatorId, currentTeamOwner } = useContext(teamsContext);
  const { data: confidoBankAccounts, getData: getConfidoBankAccountsFn } = useNoodleApi(getConfidoBankAccounts);

  useEffect(() => {
    if (creatorId && currentTeamOwner?.usesConfido) {
      getConfidoBankAccountsFn({ creatorId });
    }
  }, [getConfidoBankAccountsFn, creatorId, currentTeamOwner]);

  if (!creatorId || !currentTeamOwner?.usesConfido || !confidoBankAccounts || confidoBankAccounts.length === 0) {
    return null;
  }

  const handleChange = (newValue: string): void => {
    onChange(newValue);
  };

  const options = [
    ...(!value ? [{ title: 'Select a bank account', value: 'no-selection' }] : []),
    ...(confidoBankAccounts || []).map((account) => ({
      title: `${account.nickname} (*${account.lastFour})`,
      value: account.id,
    })),
  ];

  return (
    <div className={classNames(s.wrapper, className)}>
      <Confido />
      <CustomSelect
        options={options}
        value={value || 'no-selection'}
        id='confidoBankAccountId'
        onChange={handleChange}
        label='When you make sales of this product, the funds will be sent to this bank account:'
        native
      />
    </div>
  );
};

export default ConfidoBankAccountSelector;
